import React from 'react';
import { X, Shield, CheckCircle, Mail, Phone, MapPin, Calendar, Building2, Globe, Star } from 'lucide-react';
import { formatDate } from '../../utils/formatters';

interface User {
  id: string;
  email: string;
  fullName?: string;
  photo?: string;
  phone?: string;
  occupation?: string;
  location?: string;
  languages?: string[];
  aboutMe?: string;
  joinDate?: string;
  isVerified?: boolean;
  role: string;
  verificationStatus?: 'pending' | 'verified' | 'rejected' | null;
}

interface UserDetailsModalProps {
  user: User;
  onClose: () => void;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({ user, onClose }) => {
  const getVerificationBadge = () => {
    switch (user.verificationStatus) {
      case 'verified':
        return {
          color: 'bg-green-100 text-green-800 border-green-200',
          icon: <CheckCircle className="h-4 w-4 mr-1.5" />,
          text: 'Verified'
        };
      case 'pending':
        return {
          color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
          icon: <Shield className="h-4 w-4 mr-1.5" />,
          text: 'Verification Pending'
        };
      case 'rejected':
        return {
          color: 'bg-red-100 text-red-800 border-red-200',
          icon: <X className="h-4 w-4 mr-1.5" />,
          text: 'Verification Failed'
        };
      default:
        return {
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          icon: <Shield className="h-4 w-4 mr-1.5" />,
          text: 'Not Verified'
        };
    }
  };

  const badge = getVerificationBadge();

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-[2px] flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="relative h-32 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-white" />
          </button>
          
          <div className="absolute -bottom-12 left-6 flex items-end space-x-4">
            <div className="relative">
              <div className="h-24 w-24 rounded-xl border-4 border-white bg-white shadow-md overflow-hidden">
                {user.photo ? (
                  <img
                    src={user.photo}
                    alt={user.fullName}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                    <Shield className="h-10 w-10 text-indigo-600" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* User Info */}
        <div className="pt-16 px-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                {user.fullName || 'Unnamed User'}
              </h2>
              <div className="flex items-center space-x-3">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${badge.color}`}>
                  {badge.icon}
                  {badge.text}
                </span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  user.role === 'admin' 
                    ? 'bg-purple-100 text-purple-800 border border-purple-200'
                    : 'bg-gray-100 text-gray-800 border border-gray-200'
                }`}>
                  <Star className="h-4 w-4 mr-1.5" />
                  {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                </span>
              </div>
            </div>
          </div>

          {/* Contact & Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="space-y-4">
              <div>
                <div className="flex items-center text-sm text-gray-500 mb-1">
                  <Mail className="h-4 w-4 mr-2" />
                  Email
                </div>
                <p className="font-medium">{user.email}</p>
              </div>

              <div>
                <div className="flex items-center text-sm text-gray-500 mb-1">
                  <Phone className="h-4 w-4 mr-2" />
                  Phone
                </div>
                <p className="font-medium">{user.phone || 'Not provided'}</p>
              </div>

              <div>
                <div className="flex items-center text-sm text-gray-500 mb-1">
                  <MapPin className="h-4 w-4 mr-2" />
                  Location
                </div>
                <p className="font-medium">{user.location || 'Not provided'}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex items-center text-sm text-gray-500 mb-1">
                  <Calendar className="h-4 w-4 mr-2" />
                  Member Since
                </div>
                <p className="font-medium">
                  {user.joinDate ? formatDate(user.joinDate) : 'Unknown'}
                </p>
              </div>

              <div>
                <div className="flex items-center text-sm text-gray-500 mb-1">
                  <Building2 className="h-4 w-4 mr-2" />
                  Occupation
                </div>
                <p className="font-medium">{user.occupation || 'Not provided'}</p>
              </div>

              <div>
                <div className="flex items-center text-sm text-gray-500 mb-1">
                  <Globe className="h-4 w-4 mr-2" />
                  Languages
                </div>
                <p className="font-medium">
                  {user.languages?.length ? user.languages.join(', ') : 'Not provided'}
                </p>
              </div>
            </div>
          </div>

          {/* About Section */}
          {user.aboutMe && (
            <div className="border-t pt-6 pb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">About</h3>
              <p className="text-gray-600 whitespace-pre-wrap leading-relaxed">
                {user.aboutMe}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailsModal;